import {
  ref as dbRef, push, set, remove, get, onValue,
  query, orderByChild, limitToLast, startAt, endAt, runTransaction,
} from 'firebase/database';
import { db, auth } from '../firebase';
import { compressImage, IMAGE_PRESETS, userFacingError } from './imageCompressionUtil';
import {
  POS_CATEGORIES_NODE,
  POS_PRODUCTS_NODE,
  POS_ORDERS_NODE,
  POS_AUDIT_NODE,
  POS_AUDIT_ACTIONS,
  POS_AUDIT_LABELS,
  POS_RECENT_ORDERS_LIMIT,
  POS_SEARCH_MIN_CHARS,
} from '../constants/posConst';

// Everything the POS screens do to Firebase lives here: categories, products,
// orders and the module's own audit trail. The components never touch the
// database directly, which is what keeps the stock arithmetic — the one part
// that must be exactly right — in a single place.

/** Who is acting, for the audit trail. @returns {string} */
const currentActor = () => auth?.currentUser?.email || 'Unknown';

/**
 * Appends one entry to the POS audit trail.
 *
 * Fire-and-forget by design: a sale that succeeded must not be reported as
 * failed because its audit write did. The entry is still awaited by nothing,
 * but failures are logged so a silently broken trail is noticeable.
 *
 * @param {{action: string, entityId?: string, summary?: object}} entry
 * @returns {void}
 */
export const logPosEvent = ({ action, entityId = null, summary = null }) => {
  push(dbRef(db, POS_AUDIT_NODE), {
    action,
    entityId,
    summary: summary || null,
    performedBy: currentActor(),
    timestamp: Date.now(),
  }).catch((error) => console.error('POS audit write failed:', error));
};

// ---- Categories -----------------------------------------------------------

/**
 * Live list of category names.
 *
 * Stored as an ARRAY of strings under one key rather than a node of records:
 * the brief calls for "just a list of strings", the whole list is read
 * together everywhere it is used, and ordering is the admin's.
 *
 * @param {(categories: string[]) => void} callback
 * @returns {() => void} unsubscribe
 */
export const subscribePosCategories = (callback) => onValue(
  dbRef(db, POS_CATEGORIES_NODE),
  (snapshot) => {
    const value = snapshot.val();
    callback(Array.isArray(value) ? value.filter(Boolean) : []);
  },
  (error) => { console.error('POS categories listener failed:', error); callback([]); },
);

/**
 * Adds a category, refusing duplicates case-insensitively.
 *
 * Case-insensitive because "Drinks" and "drinks" would otherwise become two
 * tabs holding different products, which no one would notice until a product
 * went missing from the tab they expected.
 *
 * @param {string} name
 * @returns {Promise<string[]>} the saved list
 */
export const addPosCategory = async (name) => {
  const clean = String(name || '').trim();
  if (!clean) throw userFacingError('Enter a category name.');

  const snapshot = await get(dbRef(db, POS_CATEGORIES_NODE));
  const current = Array.isArray(snapshot.val()) ? snapshot.val().filter(Boolean) : [];
  if (current.some(c => c.toLowerCase() === clean.toLowerCase())) {
    throw userFacingError(`"${clean}" is already a category.`);
  }

  const next = [...current, clean];
  await set(dbRef(db, POS_CATEGORIES_NODE), next);
  logPosEvent({ action: POS_AUDIT_ACTIONS.CATEGORY_ADDED, entityId: clean, summary: { name: clean } });
  return next;
};

/**
 * Removes a category.
 *
 * Products keep the category string they were saved with — they are NOT
 * reassigned or deleted. A product whose category no longer exists still
 * appears under "All", so removing a category can never lose stock; it just
 * stops offering that tab.
 *
 * @param {string} name
 * @returns {Promise<string[]>} the saved list
 */
export const removePosCategory = async (name) => {
  const snapshot = await get(dbRef(db, POS_CATEGORIES_NODE));
  const current = Array.isArray(snapshot.val()) ? snapshot.val().filter(Boolean) : [];
  const next = current.filter(c => c !== name);
  await set(dbRef(db, POS_CATEGORIES_NODE), next);
  logPosEvent({ action: POS_AUDIT_ACTIONS.CATEGORY_REMOVED, entityId: name, summary: { name } });
  return next;
};

// ---- Products -------------------------------------------------------------

/**
 * Live list of products, each with its Firebase `id`.
 * @param {(products: object[]) => void} callback
 * @returns {() => void} unsubscribe
 */
export const subscribePosProducts = (callback) => onValue(
  dbRef(db, POS_PRODUCTS_NODE),
  (snapshot) => {
    const out = [];
    snapshot.forEach((child) => { out.push({ id: child.key, ...child.val() }); });
    out.sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
    callback(out);
  },
  (error) => { console.error('POS products listener failed:', error); callback([]); },
);

/**
 * Creates or updates a product.
 *
 * The image is compressed before storing, same as every other image in this
 * app: these records are read as a whole grid on the dashboard, and a full
 * phone photo per product would make that screen unusable on mobile data.
 *
 * @param {object|null} existing - the product being edited, or null to create
 * @param {{name: string, description: string, category: string, price: number, supplies: number, imageFile: File|null}} details
 * @returns {Promise<string>} the product's Firebase key
 */
export const savePosProduct = async (existing, details) => {
  const name = String(details.name || '').trim();
  if (!name) throw userFacingError('Give the product a name.');

  const price = Number(details.price);
  if (!Number.isFinite(price) || price < 0) throw userFacingError('Enter a price of 0 or more.');

  const supplies = Number(details.supplies);
  if (!Number.isInteger(supplies) || supplies < 0) throw userFacingError('Enter available supplies as a whole number, 0 or more.');

  // Only re-encode when a NEW file was chosen; editing a product without
  // touching its picture keeps the stored image byte for byte rather than
  // recompressing an already-compressed image every time.
  let imageUrl = existing?.imageUrl || null;
  if (details.imageFile) {
    const { dataUrl } = await compressImage(details.imageFile, IMAGE_PRESETS.PHOTO);
    imageUrl = dataUrl;
  }

  const record = {
    name,
    description: String(details.description || '').trim(),
    category: String(details.category || '').trim(),
    price,
    supplies,
    imageUrl,
    createdAt: existing?.createdAt || new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };

  const id = existing?.id || push(dbRef(db, POS_PRODUCTS_NODE)).key;
  await set(dbRef(db, `${POS_PRODUCTS_NODE}/${id}`), record);

  logPosEvent({
    action: existing ? POS_AUDIT_ACTIONS.PRODUCT_UPDATED : POS_AUDIT_ACTIONS.PRODUCT_CREATED,
    entityId: id,
    // The image is deliberately left out of the summary — a base64 photo in
    // every audit row would make the trail enormous to read back.
    summary: {
      name: record.name,
      category: record.category,
      price: record.price,
      supplies: record.supplies,
      ...(existing ? {
        before: { name: existing.name, category: existing.category, price: existing.price, supplies: existing.supplies },
      } : {}),
    },
  });

  return id;
};

/**
 * How many more of a product the till may still add to the order in progress.
 *
 * Stock MINUS what this order has already claimed, never the raw `supplies`
 * figure. Six on the shelf with four already in the basket leaves two — a cap
 * of six there would let one order promise eight, and the shortfall would only
 * surface when placePosOrder's transaction refused the whole sale at the
 * counter, with the customer waiting.
 *
 * @param {object} product - a POS product, with `id` and `supplies`
 * @param {Object<string, number>} [cart] - { [productId]: quantity } in progress
 * @returns {number} whole units still addable, never negative
 */
export const posRemainingStock = (product, cart = {}) => {
  const supplies = Math.max(0, Math.floor(Number(product?.supplies) || 0));
  const claimed = Math.max(0, Math.floor(Number(cart?.[product?.id]) || 0));
  return Math.max(0, supplies - claimed);
};

/**
 * Deletes a product. Past ORDERS are untouched — they store their own copy of
 * the name and price, so a receipt printed last week still reads correctly
 * after the product it sold has gone.
 *
 * @param {object} product
 * @returns {Promise<void>}
 */
export const deletePosProduct = async (product) => {
  if (!product?.id) throw new Error('Product id is required.');
  await remove(dbRef(db, `${POS_PRODUCTS_NODE}/${product.id}`));
  logPosEvent({
    action: POS_AUDIT_ACTIONS.PRODUCT_DELETED,
    entityId: product.id,
    summary: { name: product.name, category: product.category, price: product.price, supplies: product.supplies },
  });
};

// ---- Orders ---------------------------------------------------------------

/**
 * Takes payment for a basket: decrements stock, then records the order.
 *
 * STOCK FIRST, AND ONE TRANSACTION PER PRODUCT. Each decrement re-reads the
 * live count inside the transaction and aborts if the basket wants more than
 * is there, so two staff charging the last item at the same moment cannot
 * both succeed — a plain read-then-write would let them. Anything already
 * decremented when a later line fails is PUT BACK before throwing, so a
 * refused sale leaves stock exactly as it found it.
 *
 * The order stores its own copy of each product's name and price. Prices
 * change and products get deleted; a receipt has to keep saying what was
 * actually charged on the day.
 *
 * @param {Array<{product: object, quantity: number}>} lines
 * @param {{cashReceived?: number}} [payment] - cash tendered, for the CASH /
 *   CHANGE lines on the receipt. Omitted for a card or e-wallet sale, where
 *   printing a change figure would state something untrue about it.
 * @returns {Promise<{id: string, order: object}>}
 * @throws when a line exceeds available stock — the message names the product
 */
export const placePosOrder = async (lines, payment = {}) => {
  const basket = (lines || []).filter(l => l?.product?.id && Number(l.quantity) > 0);
  if (basket.length === 0) throw userFacingError('Add at least one product before charging.');

  const decremented = [];
  try {
    for (const { product, quantity } of basket) {
      const qty = Number(quantity);
      const stockRef = dbRef(db, `${POS_PRODUCTS_NODE}/${product.id}/supplies`);
      // eslint-disable-next-line no-await-in-loop
      const result = await runTransaction(stockRef, (current) => {
        const available = Number(current) || 0;
        // undefined ABORTS — the only way to say "not enough" from inside a
        // transaction without writing something wrong.
        if (available < qty) return undefined;
        return available - qty;
      });
      if (!result.committed) {
        throw userFacingError(`Not enough stock for ${product.name}. Please check Inventory and try again.`);
      }
      decremented.push({ product, qty });
    }
  } catch (error) {
    // Give back every unit this attempt took, so a half-applied basket never
    // leaves the shelf count short.
    await Promise.all(decremented.map(({ product, qty }) => runTransaction(
      dbRef(db, `${POS_PRODUCTS_NODE}/${product.id}/supplies`),
      (current) => (Number(current) || 0) + qty,
    ).catch(() => {})));
    throw error;
  }

  const items = basket.map(({ product, quantity }) => ({
    productId: product.id,
    name: product.name,
    category: product.category || '',
    price: Number(product.price) || 0,
    quantity: Number(quantity),
    lineTotal: (Number(product.price) || 0) * Number(quantity),
  }));

  const total = items.reduce((sum, item) => sum + item.lineTotal, 0);
  const cashReceived = Number(payment?.cashReceived);
  const paidCash = Number.isFinite(cashReceived) && cashReceived > 0;

  const order = {
    items,
    total,
    itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
    // null rather than 0 when no cash was taken: 0 would be indistinguishable
    // from a sale where the customer handed over nothing, and the receipt
    // decides whether to print CASH/CHANGE on exactly this.
    cashReceived: paidCash ? cashReceived : null,
    // Floored at 0 — a short payment must never record NEGATIVE change, which
    // would read as the till owing the customer money.
    change: paidCash ? Math.max(0, cashReceived - total) : null,
    cashier: currentActor(),
    createdAt: new Date().toISOString(),
    createdAtMs: Date.now(),
  };

  const orderRef = push(dbRef(db, POS_ORDERS_NODE));
  await set(orderRef, order);

  logPosEvent({
    action: POS_AUDIT_ACTIONS.ORDER_PLACED,
    entityId: orderRef.key,
    summary: { total: order.total, itemCount: order.itemCount, items: items.map(i => `${i.quantity}x ${i.name}`) },
  });

  return { id: orderRef.key, order };
};

/**
 * Live list of recent orders, newest first.
 *
 * Bounded by POS_RECENT_ORDERS_LIMIT: the Sales screen is a review of trading,
 * not an archive, and an unbounded node would grow without limit and be
 * re-downloaded in full on every visit.
 *
 * @param {(orders: object[]) => void} callback
 * @returns {() => void} unsubscribe
 */
export const subscribePosOrders = (callback) => onValue(
  query(dbRef(db, POS_ORDERS_NODE), orderByChild('createdAtMs'), limitToLast(POS_RECENT_ORDERS_LIMIT)),
  (snapshot) => {
    const out = [];
    snapshot.forEach((child) => { out.push({ id: child.key, ...child.val() }); });
    out.reverse();
    callback(out);
  },
  (error) => { console.error('POS orders listener failed:', error); callback([]); },
);

/**
 * Live POS audit entries, newest first.
 * @param {(entries: object[]) => void} callback
 * @returns {() => void} unsubscribe
 */
export const subscribePosAudit = (callback) => onValue(
  query(dbRef(db, POS_AUDIT_NODE), orderByChild('timestamp'), limitToLast(POS_RECENT_ORDERS_LIMIT)),
  (snapshot) => {
    const out = [];
    snapshot.forEach((child) => { out.push({ id: child.key, ...child.val() }); });
    out.reverse();
    callback(out);
  },
  (error) => { console.error('POS audit listener failed:', error); callback([]); },
);

/**
 * A 'yyyy-MM-dd' string as the first millisecond of that LOCAL day.
 *
 * Local rather than UTC because these strings come from <input type="date">,
 * which speaks the reader's own calendar day — bounding it in another zone
 * would silently move an evening's records into the wrong day.
 *
 * @param {string} value - 'yyyy-MM-dd'
 * @returns {number|null} epoch ms, or null when the string is not a date
 */
export const dayStartMs = (value) => {
  const [y, m, d] = String(value || '').split('-').map(Number);
  return (y && m && d) ? new Date(y, m - 1, d, 0, 0, 0, 0).getTime() : null;
};

/**
 * A 'yyyy-MM-dd' string as the last millisecond of that LOCAL day, so a range
 * that ends today includes everything recorded today.
 *
 * @param {string} value - 'yyyy-MM-dd'
 * @returns {number|null} epoch ms, or null when the string is not a date
 */
export const dayEndMs = (value) => {
  const [y, m, d] = String(value || '').split('-').map(Number);
  return (y && m && d) ? new Date(y, m - 1, d, 23, 59, 59, 999).getTime() : null;
};

/**
 * One audit entry flattened to the text a reader would search it by.
 *
 * VALUES only, never the field names: the summaries are keyed by things like
 * 'name' and 'items', so including keys would make a search for "name" match
 * every row and teach staff the box is useless. The values are where the
 * products, categories and prices actually live.
 *
 * @param {object} entry - an audit entry as stored under POS_AUDIT_NODE
 * @returns {string} lower-cased haystack
 */
export const posAuditSearchText = (entry) => {
  const parts = [POS_AUDIT_LABELS[entry?.action] || entry?.action || '', entry?.performedBy || ''];
  const summary = entry?.summary;

  if (summary && typeof summary === 'object') {
    Object.values(summary).forEach((value) => {
      if (value === null || value === undefined) return;
      if (Array.isArray(value)) parts.push(value.join(' '));
      // 'before' on a product update is a nested object of the old values —
      // worth searching, so the old name of a renamed product still finds it.
      else if (typeof value === 'object') parts.push(Object.values(value).join(' '));
      else parts.push(String(value));
    });
  }

  return parts.join(' ').toLowerCase();
};

/**
 * The audit entries a set of filters leaves visible.
 *
 * Filtered in the browser rather than server-side on purpose: the trail is
 * already capped at POS_RECENT_ORDERS_LIMIT entries by subscribePosAudit, so
 * this is a small in-memory list, and a live subscription cannot be re-queried
 * per keystroke without dropping and re-opening the listener.
 *
 * Every filter is optional and they compose — an empty or malformed one is
 * skipped rather than matching nothing, so a half-typed date never blanks the
 * screen.
 *
 * @param {object[]} entries - the full list, newest first
 * @param {object} [filters]
 * @param {string} [filters.action] - a POS_AUDIT_ACTIONS value; '' for all
 * @param {string} [filters.search] - free text matched against posAuditSearchText
 * @param {string} [filters.startDate] - 'yyyy-MM-dd', from 00:00 local
 * @param {string} [filters.endDate] - 'yyyy-MM-dd', to 23:59:59.999 local
 * @returns {object[]} the surviving entries, order preserved
 */
export const filterPosAuditEntries = (entries, filters = {}) => {
  const { action = '', search = '', startDate = '', endDate = '' } = filters;

  const term = String(search).trim().toLowerCase();
  const useSearch = term.length >= POS_SEARCH_MIN_CHARS;
  const from = dayStartMs(startDate);
  const to = dayEndMs(endDate);

  return (entries || []).filter((entry) => {
    if (action && entry.action !== action) return false;
    if (from !== null && Number(entry.timestamp) < from) return false;
    if (to !== null && Number(entry.timestamp) > to) return false;
    if (useSearch && !posAuditSearchText(entry).includes(term)) return false;
    return true;
  });
};

/**
 * Orders placed between two dates, inclusive of both.
 *
 * A one-shot RANGE query rather than the live subscription above: a report is
 * a question about a fixed period, and streaming it would have the numbers
 * shift under the reader as sales come in. Bounded server-side on
 * createdAtMs (see the .indexOn in database.rules.json) so a wide date range
 * doesn't pull the whole node down to filter it in the browser.
 *
 * @param {string} startDateStr - 'yyyy-MM-dd', from 00:00 local
 * @param {string} endDateStr - 'yyyy-MM-dd', to 23:59:59.999 local
 * @returns {Promise<object[]>} newest first
 */
export const fetchPosOrdersInRange = async (startDateStr, endDateStr) => {
  const from = dayStartMs(startDateStr);
  const to = dayEndMs(endDateStr);
  if (from === null || to === null) throw userFacingError('Choose a valid start and end date.');
  if (from > to) throw userFacingError('The start date is after the end date.');

  const snapshot = await get(query(
    dbRef(db, POS_ORDERS_NODE),
    orderByChild('createdAtMs'),
    startAt(from),
    endAt(to),
  ));

  const out = [];
  snapshot.forEach((child) => { out.push({ id: child.key, ...child.val() }); });
  out.reverse();
  return out;
};

/**
 * Revenue broken down by category, then by product within it.
 *
 * Mirrors the booking export's Source -> Component table: the category is the
 * source, each product a component under it. Shares are of the grand total,
 * so the components sum to 100% across the whole report rather than within
 * their own category.
 *
 * @param {object[]} orders
 * @returns {{total: number, itemCount: number, orderCount: number, groups: Array<{name: string, revenue: number, share: number, products: Array<{name: string, revenue: number, quantity: number, share: number}>}>}}
 */
export const summarisePosSales = (orders) => {
  const byCategory = new Map();
  let total = 0;
  let itemCount = 0;

  (orders || []).forEach((order) => {
    (order?.items || []).forEach((item) => {
      const revenue = Number(item.lineTotal) || 0;
      const quantity = Number(item.quantity) || 0;
      total += revenue;
      itemCount += quantity;

      // Products saved before categories existed, or whose category was
      // removed, still have to appear somewhere or the table stops adding up.
      const categoryName = item.category || 'Uncategorised';
      const group = byCategory.get(categoryName)
        || { name: categoryName, revenue: 0, products: new Map() };
      group.revenue += revenue;

      const key = item.productId || item.name;
      const product = group.products.get(key) || { name: item.name, revenue: 0, quantity: 0 };
      product.revenue += revenue;
      product.quantity += quantity;
      product.name = item.name || product.name;
      group.products.set(key, product);
      byCategory.set(categoryName, group);
    });
  });

  const share = (value) => (total > 0 ? (value / total) * 100 : 0);

  const groups = [...byCategory.values()]
    .map(group => ({
      name: group.name,
      revenue: group.revenue,
      share: share(group.revenue),
      products: [...group.products.values()]
        .map(p => ({ ...p, share: share(p.revenue) }))
        .sort((a, b) => b.revenue - a.revenue),
    }))
    .sort((a, b) => b.revenue - a.revenue);

  return { total, itemCount, orderCount: (orders || []).length, groups };
};

/**
 * Units sold per product across the given orders, best first.
 *
 * Keyed by productId with the name carried alongside, so a renamed product
 * stays one bar rather than splitting into two.
 *
 * @param {object[]} orders
 * @param {number} limit
 * @returns {Array<{productId: string, name: string, quantity: number, revenue: number}>}
 */
export const rankBestSellers = (orders, limit) => {
  const totals = new Map();
  (orders || []).forEach((order) => {
    (order?.items || []).forEach((item) => {
      const key = item.productId || item.name;
      const row = totals.get(key) || { productId: key, name: item.name, quantity: 0, revenue: 0 };
      row.quantity += Number(item.quantity) || 0;
      row.revenue += Number(item.lineTotal) || 0;
      // The most recent name wins, so the chart matches what the product is
      // called today.
      row.name = item.name || row.name;
      totals.set(key, row);
    });
  });
  return [...totals.values()]
    .sort((a, b) => b.quantity - a.quantity)
    .slice(0, limit);
};
